'use client';

import type { MysteryRoomDoorRuntimeState, MysteryRoomHeroState, MysteryRoomNpcState, MysteryRoomNpcTaskKind } from '../core/localRuntime';
import type { MysteryRoomMapDefinition, MysteryRoomSegment, MysteryRoomVec2 } from '../world/mapTypes';
import { resolveZoneId, facingFromVelocity, stepActor } from './movementSystem';
import { normalizeInputVector } from './inputSystem';
import { buildActorRouteToPoint, resolveImmediateRouteWaypoint } from './navigationSystem';
import { roleActionForTask } from './npcTaskSystem';
import { npcCanTargetNpc, npcUsesZombieBehavior } from './npcBehaviorTreeSystem';
import { buildSightBlockingSegments, hasUnobstructedSightLine } from './visibilitySystem';

const TARGET_MARGIN = 8;
const SUSPICIOUS_SCAN_STAGE_MS = 180;
const SUSPICIOUS_SCAN_CYCLE_MS = SUSPICIOUS_SCAN_STAGE_MS * 4;
const BUILTIN_MAP_ID = 'lisad_9_night_shift';

export function updateNpcBehavior(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
  context: { hero?: MysteryRoomHeroState; npcs?: MysteryRoomNpcState[] } = {},
): MysteryRoomNpcState {
  if (npcUsesZombieBehavior(npc)) {
    return updateZombieBehavior(npc, map, doors, dtMs, context);
  }

  if (npc.currentRoleAction && npc.roleActionRemainingMs > 0) {
    const roleActionRemainingMs = Math.max(0, npc.roleActionRemainingMs - dtMs);
    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      currentRoleAction: roleActionRemainingMs === 0 ? null : npc.currentRoleAction,
      roleActionRemainingMs,
    };
  }

  if (npc.currentTask?.kind === 'trigger_alarm') {
    return clearSuspiciousScan(updateNpcAssignedTask(npc, map, doors, dtMs));
  }

  if (npc.awarenessState === 'suspicious') {
    return updateNpcSuspicious(npc, dtMs);
  }

  if (npc.awarenessState === 'alert' && npc.investigateTarget) {
    return clearSuspiciousScan(moveNpcTowardTarget(npc, map, doors, dtMs, npc.investigateTarget, 'alert'));
  }

  if (npc.awarenessState === 'search' && npc.investigateTarget) {
    return clearSuspiciousScan(updateNpcSearch(npc, map, doors, dtMs));
  }

  const taskPointPatrolActive = npc.currentTask?.kind === 'patrol' && npc.currentTask.anchorId && map.id !== BUILTIN_MAP_ID;
  if (
    npc.currentTask &&
    (npc.currentTask.kind !== 'patrol' ||
      taskPointPatrolActive ||
      (!npc.routeId && (npc.currentTask.targetPoint || npc.currentTask.targetNodeId)))
  ) {
    return clearSuspiciousScan(updateNpcAssignedTask(npc, map, doors, dtMs));
  }

  return clearSuspiciousScan(updateNpcPatrol(
    npc,
    map,
    doors,
    dtMs,
  ));
}

function updateNpcAssignedTask(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
) {
  if (!npc.currentTask) {
    return npc;
  }

  const incidentTargetNode =
    npc.currentTask.assignedIncidentId && npc.currentTargetNodeId
      ? map.navNodes.find((node) => node.id === npc.currentTargetNodeId) ?? null
      : null;
  const incidentTargetPoint = npc.currentTask.assignedIncidentId && npc.currentTask.targetPoint
    ? npc.currentTask.targetPoint
    : null;
  const hasReachedIncidentTarget =
    (incidentTargetNode !== null &&
      npc.navPath.length > 0 &&
      npc.navIndex >= npc.navPath.length - 1 &&
      Math.hypot(
        incidentTargetNode.position.x - npc.position.x,
        incidentTargetNode.position.y - npc.position.y,
      ) <= npc.radius + TARGET_MARGIN) ||
    (incidentTargetPoint !== null &&
      Math.hypot(
        incidentTargetPoint.x - npc.position.x,
        incidentTargetPoint.y - npc.position.y,
      ) <= npc.radius + TARGET_MARGIN);

  if (hasReachedIncidentTarget && npc.currentRoleAction === null) {
    const nextRoleAction = roleActionForTask(npc, npc.currentTask);
    if (nextRoleAction) {
      return {
        ...npc,
        velocity: { x: 0, y: 0 },
        animationState: 'idle' as const,
        currentRoleAction: nextRoleAction,
        roleActionRemainingMs: roleActionDurationMs(npc.currentTask.kind),
      };
    }
  }

  return updateNpcTaskRoute(npc, map, doors, dtMs);
}

function roleActionDurationMs(taskKind: NonNullable<MysteryRoomNpcState['currentTask']>['kind']) {
  switch (taskKind) {
    case 'repair_server':
    case 'repair_door':
      return 1_800;
    case 'trigger_alarm':
      return 900;
    case 'investigate':
      return 1_600;
    default:
      return 1_200;
  }
}

function updateNpcSuspicious(
  npc: MysteryRoomNpcState,
  dtMs: number,
): MysteryRoomNpcState {
  const baseFacing = npc.investigateTarget
    ? facingFromVelocity(
        {
          x: npc.investigateTarget.x - npc.position.x,
          y: npc.investigateTarget.y - npc.position.y,
        },
        npc.facing,
      )
    : npc.facing;
  const suspiciousScanElapsedMs = npc.investigateTarget
    ? npc.suspiciousScanElapsedMs + dtMs
    : 0;

  return {
    ...npc,
    velocity: { x: 0, y: 0 },
    facing: suspiciousScanFacing(baseFacing, suspiciousScanElapsedMs),
    animationState: 'idle',
    suspiciousScanElapsedMs,
  };
}

function clearSuspiciousScan(
  npc: MysteryRoomNpcState,
): MysteryRoomNpcState {
  if (npc.suspiciousScanElapsedMs === 0) {
    return npc;
  }

  return {
    ...npc,
    suspiciousScanElapsedMs: 0,
  };
}

function suspiciousScanFacing(
  baseFacing: MysteryRoomNpcState['facing'],
  scanElapsedMs: number,
): MysteryRoomNpcState['facing'] {
  const stage = Math.floor((scanElapsedMs % SUSPICIOUS_SCAN_CYCLE_MS) / SUSPICIOUS_SCAN_STAGE_MS);

  switch (stage) {
    case 1:
      return rotateFacingClockwise(baseFacing);
    case 3:
      return rotateFacingCounterClockwise(baseFacing);
    case 0:
    case 2:
    default:
      return baseFacing;
  }
}

function rotateFacingClockwise(
  facing: MysteryRoomNpcState['facing'],
): MysteryRoomNpcState['facing'] {
  switch (facing) {
    case 'up':
      return 'right';
    case 'right':
      return 'down';
    case 'down':
      return 'left';
    case 'left':
      return 'up';
  }
}

function rotateFacingCounterClockwise(
  facing: MysteryRoomNpcState['facing'],
): MysteryRoomNpcState['facing'] {
  switch (facing) {
    case 'up':
      return 'left';
    case 'left':
      return 'down';
    case 'down':
      return 'right';
    case 'right':
      return 'up';
  }
}

function updateNpcPatrol(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
): MysteryRoomNpcState {
  const route = map.patrolRoutes.find((entry) => entry.id === npc.routeId);
  if (!route || route.nodes.length === 0) {
    return npc;
  }

  let nextNpc = { ...npc };
  let remainingMs = dtMs;
  let waypointTransitionsRemaining = Math.max(1, route.nodes.length * 2);

  while (remainingMs > 0 && waypointTransitionsRemaining > 0) {
    if (nextNpc.pauseRemainingMs > 0) {
      const spentPauseMs = Math.min(remainingMs, nextNpc.pauseRemainingMs);
      nextNpc = {
        ...nextNpc,
        pauseRemainingMs: nextNpc.pauseRemainingMs - spentPauseMs,
        velocity: { x: 0, y: 0 },
        animationState: 'idle',
      };
      remainingMs -= spentPauseMs;
      continue;
    }

    const node = route.nodes[nextNpc.routeNodeIndex];
    const delta = {
      x: node.x - nextNpc.position.x,
      y: node.y - nextNpc.position.y,
    };
    const distance = Math.hypot(delta.x, delta.y);

    if (distance <= nextNpc.radius + 8) {
      waypointTransitionsRemaining -= 1;
      nextNpc = {
        ...nextNpc,
        zoneId: node.zoneId,
        routeNodeIndex: (nextNpc.routeNodeIndex + 1) % route.nodes.length,
        pauseRemainingMs: node.pauseMs,
        velocity: { x: 0, y: 0 },
        animationState: 'idle',
      };
      if (waypointTransitionsRemaining <= 0) {
        return {
          ...nextNpc,
          velocity: { x: 0, y: 0 },
          animationState: 'idle',
        };
      }
      continue;
    }

    const direction = normalizeInputVector(delta);
    const velocity = {
      x: direction.x * nextNpc.speed,
      y: direction.y * nextNpc.speed,
    };
    const maxTravelDistance = nextNpc.speed * (remainingMs / 1000);
    const travelDistance = Math.min(maxTravelDistance, distance);
    const travelSeconds = travelDistance / nextNpc.speed;
    const nextPosition = stepActor(map, nextNpc, velocity, travelSeconds, doors);

    if (nextPosition.x === nextNpc.position.x && nextPosition.y === nextNpc.position.y) {
      nextNpc = {
        ...nextNpc,
        velocity: { x: 0, y: 0 },
        animationState: 'idle',
      };
      break;
    }

    nextNpc = {
      ...nextNpc,
      position: nextPosition,
      velocity,
      facing: facingFromVelocity(velocity, nextNpc.facing),
      zoneId: resolveZoneId(map, nextPosition),
      animationState: 'walk',
    };
    remainingMs = 0;
  }

  return nextNpc;
}

function updateNpcSearch(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
): MysteryRoomNpcState {
  if (!npc.investigateTarget) {
    return {
      ...npc,
      awarenessState: 'patrol',
      searchRemainingMs: 0,
    };
  }

  const distance = Math.hypot(
    npc.investigateTarget.x - npc.position.x,
    npc.investigateTarget.y - npc.position.y,
  );

  if (distance <= npc.radius + TARGET_MARGIN) {
    const searchRemainingMs = Math.max(0, npc.searchRemainingMs - dtMs);
    if (searchRemainingMs === 0) {
      return {
        ...npc,
        velocity: { x: 0, y: 0 },
        animationState: 'idle',
        awarenessState: 'patrol',
        investigateTarget: null,
        searchRemainingMs: 0,
      };
    }

    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      awarenessState: 'search',
      searchRemainingMs,
    };
  }

  return moveNpcTowardTarget(npc, map, doors, dtMs, npc.investigateTarget, 'search');
}

function updateNpcTaskRoute(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
): MysteryRoomNpcState {
  const currentTargetNodeId = npc.navPath[npc.navIndex] ?? npc.navPath[0] ?? null;
  const currentTargetNode = currentTargetNodeId
    ? map.navNodes.find((node) => node.id === currentTargetNodeId)
    : null;
  const pointTarget = npc.currentTask?.targetPoint ?? null;

  if (!currentTargetNode && pointTarget) {
    return updateNpcPointTaskRoute(npc, map, doors, dtMs, pointTarget);
  }

  if (!currentTargetNode) {
    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
    };
  }

  const distance = Math.hypot(
    currentTargetNode.position.x - npc.position.x,
    currentTargetNode.position.y - npc.position.y,
  );

  if (distance <= npc.radius + TARGET_MARGIN) {
    if (pointTarget && npc.navIndex >= Math.max(0, npc.navPath.length - 1)) {
      return updateNpcPointTaskRoute(
        {
          ...npc,
          navPath: [],
          navIndex: 0,
          currentTargetNodeId: null,
        },
        map,
        doors,
        dtMs,
        pointTarget,
      );
    }

    const nextNavIndex = Math.min(npc.navIndex + 1, Math.max(0, npc.navPath.length - 1));

    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      navIndex: nextNavIndex,
      zoneId: currentTargetNode.zoneId,
      stuckMs: 0,
      lastWaypointDistance: distance,
    };
  }

  const direction = normalizeInputVector({
    x: currentTargetNode.position.x - npc.position.x,
    y: currentTargetNode.position.y - npc.position.y,
  });
  const velocity = {
    x: direction.x * npc.speed,
    y: direction.y * npc.speed,
  };
  const travelDistance = Math.min(npc.speed * (dtMs / 1000), distance);
  const nextPosition = stepActor(map, npc, velocity, travelDistance / npc.speed, doors);

  if (nextPosition.x === npc.position.x && nextPosition.y === npc.position.y) {
    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      lastWaypointDistance: distance,
    };
  }

  return {
    ...npc,
    position: nextPosition,
    velocity,
    facing: facingFromVelocity(velocity, npc.facing),
    zoneId: resolveZoneId(map, nextPosition),
    animationState: 'walk',
    stuckMs: 0,
    lastWaypointDistance: distance,
  };
}

function updateNpcPointTaskRoute(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
  target: { x: number; y: number },
): MysteryRoomNpcState {
  const distance = Math.hypot(target.x - npc.position.x, target.y - npc.position.y);

  if (distance <= npc.radius + TARGET_MARGIN) {
    if (npc.pauseRemainingMs > 0) {
      const pauseRemainingMs = Math.max(0, npc.pauseRemainingMs - dtMs);
      const task = npc.currentTask;
      const nextTask =
        pauseRemainingMs === 0 && task && routineTaskCanScatter(task.kind) && task.anchorPoint
          ? {
              ...task,
              targetPoint: pickRoutineTaskPoint(task, npc, map, doors),
            }
          : task;

      return {
        ...npc,
        pauseRemainingMs,
        velocity: { x: 0, y: 0 },
        animationState: 'idle',
        currentTask: nextTask,
      };
    }

    const task = npc.currentTask;
    if (task && routineTaskCanScatter(task.kind) && task.anchorPoint) {
      return {
        ...npc,
        velocity: { x: 0, y: 0 },
        animationState: 'idle',
        pauseRemainingMs: routineDwellMs(npc, task.kind),
      };
    }

    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
    };
  }

  return moveNpcTowardTarget(npc, map, doors, dtMs, target, npc.awarenessState);
}

function updateZombieBehavior(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
  context: { hero?: MysteryRoomHeroState; npcs?: MysteryRoomNpcState[] },
): MysteryRoomNpcState {
  const blockingSegments = buildSightBlockingSegments(map, doors);
  const target = pickZombieTarget(npc, context, blockingSegments);
  if (target) {
    return moveNpcTowardTarget(npc, map, doors, dtMs, target, 'alert');
  }

  const wanderTarget = npc.investigateTarget && Math.hypot(
    npc.investigateTarget.x - npc.position.x,
    npc.investigateTarget.y - npc.position.y,
  ) > npc.radius + 18
    ? npc.investigateTarget
    : zombieWanderPoint(npc);

  return {
    ...moveNpcTowardTarget(
      {
        ...npc,
        investigateTarget: wanderTarget,
      },
      map,
      doors,
      dtMs,
      wanderTarget,
      'patrol',
    ),
    awarenessState: 'patrol',
  };
}

function pickZombieTarget(
  npc: MysteryRoomNpcState,
  context: { hero?: MysteryRoomHeroState; npcs?: MysteryRoomNpcState[] },
  blockingSegments: MysteryRoomSegment[],
): MysteryRoomVec2 | null {
  let best: { position: MysteryRoomVec2; distance: number } | null = null;
  const candidates: MysteryRoomVec2[] = [];
  if (context.hero && !context.hero.hidden) {
    candidates.push(context.hero.position);
  }
  for (const other of context.npcs ?? []) {
    if (!npcCanTargetNpc(npc, other)) {
      continue;
    }
    candidates.push(other.position);
  }

  for (const position of candidates) {
    const distance = Math.hypot(position.x - npc.position.x, position.y - npc.position.y);
    if (distance > npc.visionRange) {
      continue;
    }
    if (!hasUnobstructedSightLine(npc.position, position, blockingSegments)) {
      continue;
    }
    if (!best || distance < best.distance) {
      best = { position, distance };
    }
  }

  return best ? { ...best.position } : null;
}

function routineTaskCanScatter(taskKind: MysteryRoomNpcTaskKind) {
  return taskKind === 'work' || taskKind === 'rest' || taskKind === 'patrol' || taskKind === 'toilet';
}

function routineDwellMs(npc: MysteryRoomNpcState, taskKind: MysteryRoomNpcTaskKind) {
  const seed = stableHash(`${npc.id}:${taskKind}:${npc.position.x}:${npc.position.y}`);
  if (taskKind === 'toilet') {
    return 5_000 + (seed % 9_000);
  }
  if (taskKind === 'rest') {
    return 8_000 + (seed % 27_000);
  }
  if (taskKind === 'patrol') {
    return 2_000 + (seed % 6_000);
  }
  return 3_000 + (seed % 17_000);
}

function routineScatterPoint(
  anchor: MysteryRoomVec2,
  npc: MysteryRoomNpcState,
  task: NonNullable<MysteryRoomNpcState['currentTask']>,
  attempt = 0,
) {
  const radius = Math.max(npc.radius + TARGET_MARGIN + 4, task.taskAreaRadius ?? defaultTaskAreaRadius(task.kind));
  const seed = stableHash(`${npc.id}:${task.kind}:${Math.round(npc.position.x)}:${Math.round(npc.position.y)}:${attempt}`);
  const angle = (seed % 628) / 100;
  const minDistance = Math.min(radius, npc.radius + TARGET_MARGIN + (task.kind === 'toilet' ? 6 : 18));
  if (task.taskAreaShape === 'square') {
    const halfSide = radius;
    const xSign = (seed & 1) === 0 ? -1 : 1;
    const ySign = (seed & 2) === 0 ? -1 : 1;
    const x = xSign * (minDistance + Math.max(0, halfSide - minDistance) * (((seed >> 4) % 100) / 100));
    const y = ySign * (minDistance + Math.max(0, halfSide - minDistance) * (((seed >> 11) % 100) / 100));
    return {
      x: Math.round(anchor.x + x),
      y: Math.round(anchor.y + y),
    };
  }

  const distance = minDistance + Math.max(0, radius - minDistance) * (((seed >> 4) % 100) / 100);
  return {
    x: Math.round(anchor.x + Math.cos(angle) * distance),
    y: Math.round(anchor.y + Math.sin(angle) * distance),
  };
}

function pickRoutineTaskPoint(
  task: NonNullable<MysteryRoomNpcState['currentTask']>,
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
) {
  if (!task.anchorPoint) {
    return task.targetPoint ?? npc.position;
  }

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const point = routineScatterPoint(task.anchorPoint, npc, task, attempt);
    const route = buildActorRouteToPoint(
      map,
      doors,
      {
        accessLevels: npc.accessLevels,
        actorType: 'npc',
        position: npc.position,
        zoneId: npc.zoneId,
      },
      point,
      npc.radius,
    );
    if (route.reachable && Math.hypot(point.x - npc.position.x, point.y - npc.position.y) > npc.radius + TARGET_MARGIN) {
      return point;
    }
  }

  return { ...task.anchorPoint };
}

function defaultTaskAreaRadius(taskKind: MysteryRoomNpcTaskKind) {
  return taskKind === 'toilet' ? 28 : taskKind === 'rest' ? 96 : 84;
}

function zombieWanderPoint(npc: MysteryRoomNpcState) {
  const seed = stableHash(`${npc.id}:zombie:${Math.round(npc.position.x / 32)}:${Math.round(npc.position.y / 32)}`);
  const angle = (seed % 628) / 100;
  const distance = 90 + (seed % 140);
  return {
    x: Math.round(npc.position.x + Math.cos(angle) * distance),
    y: Math.round(npc.position.y + Math.sin(angle) * distance),
  };
}

function stableHash(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function moveNpcTowardTarget(
  npc: MysteryRoomNpcState,
  map: MysteryRoomMapDefinition,
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
  target: { x: number; y: number },
  awarenessState: MysteryRoomNpcState['awarenessState'],
): MysteryRoomNpcState {
  const route = buildActorRouteToPoint(
    map,
    doors,
    {
      accessLevels: npc.accessLevels,
      actorType: 'npc',
      position: npc.position,
      zoneId: npc.zoneId,
    },
    target,
    npc.radius,
  );
  if (!route.reachable) {
    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      awarenessState,
    };
  }
  const activeTarget =
    resolveImmediateRouteWaypoint(route, npc.position, npc.radius + TARGET_MARGIN) ?? target;
  const delta = {
    x: activeTarget.x - npc.position.x,
    y: activeTarget.y - npc.position.y,
  };
  const distance = Math.hypot(delta.x, delta.y);

  if (distance <= npc.radius + TARGET_MARGIN) {
    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      awarenessState,
    };
  }

  const direction = normalizeInputVector(delta);
  const velocity = {
    x: direction.x * npc.speed,
    y: direction.y * npc.speed,
  };
  const travelDistance = Math.min(npc.speed * (dtMs / 1000), distance);
  const nextPosition = stepActor(map, npc, velocity, travelDistance / npc.speed, doors);

  if (nextPosition.x === npc.position.x && nextPosition.y === npc.position.y) {
    return {
      ...npc,
      velocity: { x: 0, y: 0 },
      animationState: 'idle',
      awarenessState,
    };
  }

  return {
    ...npc,
    position: nextPosition,
    velocity,
    facing: facingFromVelocity(velocity, npc.facing),
    zoneId: resolveZoneId(map, nextPosition),
    animationState: 'walk',
    awarenessState,
  };
}
